// Read layer for the Results tab — the user's RESOLVED + OPEN predictions.
//
// Everything is already on the answer doc: the resolveQuestion trigger stamps
// correctAnswer + isCorrect + resolvedAt onto users/{uid}/answers/{qid}; the
// question's frozen community % + estimatedResolutionDate were copied at submit.
// So Results is a pure read — no new Cloud Function.
//
// Resolved-outcome data (incl. how the crowd did) IS allowed on web; only the
// LIVE pre-resolution consensus is iOS-only (plan §5).

import { db } from '../firebase.js';

const ANSWERS_LIMIT = 150;

// The six categories + emoji glyphs (web stand-in for the iOS SF Symbols),
// in the canonical app order.
export const CATEGORIES = [
  { key: 'Sports', emoji: '🏈' },
  { key: 'Markets', emoji: '📈' },
  { key: 'Politics', emoji: '🏛️' },
  { key: 'Culture', emoji: '🎬' },
  { key: 'Discovery', emoji: '🔭' },
  { key: 'World', emoji: '🌍' },
];

function decodeAnswer(id, data) {
  const resolvedAt = data.resolvedAt?.toDate?.() ?? null;
  return {
    id,
    questionId: data.questionId ?? id,
    text: data.questionText ?? '',
    category: data.category ?? 'Markets',
    answer: typeof data.answer === 'boolean' ? data.answer : null,
    isSkipped: data.isSkipped === true,
    isDoubleDown: data.isDoubleDown === true,
    correctAnswer:
      typeof data.correctAnswer === 'boolean' ? data.correctAnswer : null,
    isCorrect: data.isCorrect === true,
    isVoided: data.isVoided === true,
    communityYesPercent:
      typeof data.communityYesPercent === 'number'
        ? data.communityYesPercent
        : null,
    // The crowd's line as FROZEN AT LOCK, mirrored onto the answer by
    // resolveQuestion (2026-08-05) so the crowd-beat math needs no second
    // read. Distinct from `communityYesPercent` above, which this doc
    // captured at SUBMIT time. Null on anything resolved before that ship —
    // util/crowdBeat.js falls back.
    crowdYesPercent:
      typeof data.crowdYesPercent === 'number' ? data.crowdYesPercent : null,
    resolvedAt,
    isResolved: resolvedAt !== null,
    estimatedResolutionDate: data.estimatedResolutionDate?.toDate?.() ?? null,
    // Freshness + surface, for the reveal ordering (Uncle Phil, 2026-08-07):
    // submittedAt = when THIS player made the call — the truest "fresh in
    // mind" signal; lane separates the six from section answers.
    submittedAt: data.submittedAt?.toDate?.() ?? null,
    lane: typeof data.lane === 'string' ? data.lane : '',
  };
}

/**
 * Fetch the user's recent answers (resolved + open), split for the two toggles.
 * @returns {Promise<{resolved: Array, open: Array}>}
 */
export async function fetchAnswersForResults(uid) {
  if (!uid) return { resolved: [], open: [], truncated: false };
  const snap = await db
    .collection('users')
    .doc(uid)
    .collection('answers')
    .orderBy('submittedAt', 'desc')
    .limit(ANSWERS_LIMIT)
    .get();
  const all = snap.docs.map((d) => decodeAnswer(d.id, d.data()));
  // We read only the most recent ANSWERS_LIMIT answers; when we hit that cap
  // older predictions are not included, so callers can disclose the window
  // (audit B3).
  const truncated = snap.size === ANSWERS_LIMIT;
  // FRESH MEMORIES LEAD THE REVEAL (Uncle Phil via Tom, 2026-08-07 —
  // LOCKSTEP with iOS ResultsView.displayedQuestions). Nights still run
  // newest-first, but WITHIN a night the freshest call leads: sorting on
  // bare resolvedAt let a weeks-old long-fuse question open the reveal
  // above yesterday's six purely because the resolver processed it last,
  // and the reveal's "did I call it?" beat only fires on a prediction the
  // player still remembers making. Old resolutions close the night as
  // "oh, THAT finally resolved" bonuses instead. Order: ET night desc →
  // submittedAt desc (iOS keys on publishDate; same day in practice) →
  // the six before sections → resolvedAt as the stable tiebreak.
  const resolved = all
    .filter((a) => a.isResolved)
    .sort((a, b) => {
      const dayA = a.resolvedAt ? etKey(a.resolvedAt) : '';
      const dayB = b.resolvedAt ? etKey(b.resolvedAt) : '';
      if (dayA !== dayB) return dayA < dayB ? 1 : -1;
      const subA = a.submittedAt?.getTime() ?? 0;
      const subB = b.submittedAt?.getTime() ?? 0;
      if (subA !== subB) return subB - subA;
      const secA = a.lane === 'section' ? 1 : 0;
      const secB = b.lane === 'section' ? 1 : 0;
      if (secA !== secB) return secA - secB;
      return (b.resolvedAt?.getTime() ?? 0) - (a.resolvedAt?.getTime() ?? 0);
    });
  const open = all
    .filter((a) => !a.isResolved && !a.isSkipped)
    .sort(
      (a, b) =>
        (a.estimatedResolutionDate?.getTime() ?? 0) -
        (b.estimatedResolutionDate?.getTime() ?? 0)
    );
  return { resolved, open, truncated };
}

/** The newest resolvedAt across the user's answers, in millis (badge), 0 if none. */
export async function latestResolvedAtMillis(uid) {
  if (!uid) return 0;
  const snap = await db
    .collection('users')
    .doc(uid)
    .collection('answers')
    .orderBy('resolvedAt', 'desc')
    .limit(1)
    .get();
  if (snap.empty) return 0;
  return snap.docs[0].data().resolvedAt?.toMillis?.() ?? 0;
}

/**
 * Group resolved answers by ET resolvedAt day, preserving desc order.
 *
 * Each group also carries that day's RECORD — `correct` / `total` over the
 * scored picks in it (non-skipped, non-voided, actually answered), which is
 * the "Thu, Aug 5 — 4/6" the day header reads (web parity with the iOS
 * `ResultDayGroup`, 2026-08-05). The record describes the rows in the group,
 * so a caller that pre-filters the list gets a record of what it filtered to
 * — which is why Results suppresses it under "celebrations only", where the
 * misses aren't in the list to be counted.
 */
export function groupByDay(answers) {
  const groups = [];
  const byKey = new Map();
  for (const a of answers) {
    if (!a.resolvedAt) continue;
    const key = etKey(a.resolvedAt);
    if (!byKey.has(key)) {
      const g = { key, label: etLabel(a.resolvedAt), items: [], correct: 0, total: 0 };
      byKey.set(key, g);
      groups.push(g);
    }
    const g = byKey.get(key);
    g.items.push(a);
    if (!a.isVoided && !a.isSkipped && typeof a.answer === 'boolean' && a.correctAnswer !== null) {
      g.total += 1;
      if (a.isCorrect) g.correct += 1;
    }
  }
  return groups;
}

/**
 * The most-recent resolved batch's score — { correct, total } over the answers
 * resolved on the latest resolvedAt day (non-voided, answered). Drives the
 * "PLAY REVEAL" card's X/N. null if there are no resolved answers.
 */
export function mostRecentBatchSummary(resolved) {
  if (!resolved.length) return null;
  const latestKey = etKey(resolved[0].resolvedAt);
  const batch = resolved.filter(
    (a) =>
      a.resolvedAt &&
      etKey(a.resolvedAt) === latestKey &&
      !a.isVoided &&
      typeof a.answer === 'boolean' &&
      a.correctAnswer !== null
  );
  return {
    correct: batch.filter((a) => a.isCorrect).length,
    total: batch.length,
    label: etLabel(resolved[0].resolvedAt),
  };
}

export function etKey(date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function etLabel(date) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}
